import { LoadingOutlined } from '@ant-design/icons'
import { Table } from 'antd'
import { TableProps } from 'antd/lib'
import { useState, useEffect } from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import styled from 'styled-components'

import { apiRequest, apiStreamReader } from '../../clients/apiRequest'
import { AuthLayout } from '../../components/AuthLayout'
import { Icon } from '../../components/Icon'
import { PageBody } from '../../components/PageBody'
import { PageLabel } from '../../components/PageLabel'
import { Spacer } from '../../components/Spacer'
import { TabContainer } from '../../components/TabContainer'
import { Tooltip } from '../../components/Tooltip'
import { selectUserToken } from '../../store/user'
import { SPACING } from '../../styles/mixins/constants'
import { device } from '../../styles/mixins/device'
import { theme } from '../../styles/theme'
import { downloadCSV } from '../../util/file'
import { decodeUTF8 } from '../../util/string'

const Container = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  flex-flow: column nowrap;
`

const StyledPageBody = styled(PageBody)`
  overflow: auto;
  min-height: 0px;
  flex: 1 1 auto;
`

const HeaderGrid = styled.div`
  display: grid;
  grid-gap: ${SPACING[3]};
  grid-template-areas: 'label' 'yardreport' 'filters';
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  ${device.desktop`
    grid-template-columns: 1fr auto;
    grid-template-areas: 'label yardreport' 'filters filters';
    grid-row-gap: ${SPACING[6]};
    `}
`

const StyledPageLabel = styled(PageLabel)`
  grid-area: label;
`

const StyledTableContainer = styled.div`
  border: 1px solid ${theme.colors.border.muted};
  max-width: 756px;
`

type ISRTableDataType = {
  key: string
  type?: string
  diesel?: string
  gasoline?: string
  nze?: string
  ze?: string
}

export function ISR2305Report(): JSX.Element {
  const token = useSelector(selectUserToken, shallowEqual)

  const [data, setData] = useState<any>(null)
  const [isLoading, setLoading] = useState(false)

  useEffect(() => {
    const fetchReportData = async (): Promise<void> => {
      if (token) {
        setLoading(true)

        const result = await apiRequest({
          method: 'POST',
          path: '/observations/isr',
          token: token || '<TOKEN>',
        })
        setData(result?.data)
        setLoading(false)
      }
    }

    fetchReportData()
  }, [token])

  const isr2305Stats = data || []

  return (
    <AuthLayout>
      <StyledPageBody>
        <Container>
          <HeaderGrid>
            <StyledPageLabel title="ISR 2305" description="Report in compliance with ISR 2305." />
          </HeaderGrid>
          <Spacer vertical size={0} />
          <TabContainer
            activeKey="isr2305.truckTripLog"
            tabs={[
              {
                title: 'Truck Trip Log',
                key: 'isr2305.truckTripLog',
                Component: isLoading ? (
                  <LoadingOutlined />
                ) : (
                  <ISR2305ReportTable data={isr2305Stats} />
                ),
              },
            ]}
          />
        </Container>
      </StyledPageBody>
    </AuthLayout>
  )
}

const ISR2305ReportTable = (props: any): JSX.Element => {
  const token = useSelector(selectUserToken, shallowEqual)
  const isr2305ReportStats = props.data
  const NZE = ['CNG', 'RNG', 'P']
  const ZE = ['EB', 'FCH']
  const WEIGHTED_FACTOR = 2.5

  const countWhere = (statsArray: Array<any>, condition: (statObject: any) => boolean) => {
    return statsArray.filter(condition).reduce((sum, stat) => sum + parseInt(stat.count), 0)
  }
  const isr2305ReportTableData: any = {}

  isr2305ReportTableData['Class 2b-7'] = {
    D: countWhere(isr2305ReportStats, (stat) => stat.weightClass < '8' && stat.fuelType === 'D'),
    G: countWhere(isr2305ReportStats, (stat) => stat.weightClass < '8' && stat.fuelType === 'G'),
    NZE: countWhere(
      isr2305ReportStats,
      (stat) => stat.weightClass < '8' && NZE.includes(stat.fuelType)
    ),
    ZE: countWhere(
      isr2305ReportStats,
      (stat) => stat.weightClass < '8' && ZE.includes(stat.fuelType)
    ),
  }
  isr2305ReportTableData['Class 8'] = {
    D: countWhere(isr2305ReportStats, (stat) => stat.weightClass === '8' && stat.fuelType === 'D'),
    G: countWhere(isr2305ReportStats, (stat) => stat.weightClass === '8' && stat.fuelType === 'G'),
    NZE: countWhere(
      isr2305ReportStats,
      (stat) => stat.weightClass === '8' && NZE.includes(stat.fuelType)
    ),
    ZE: countWhere(
      isr2305ReportStats,
      (stat) => stat.weightClass === '8' && ZE.includes(stat.fuelType)
    ),
  }
  isr2305ReportTableData.WATTs = {
    D:
      WEIGHTED_FACTOR *
      (isr2305ReportTableData['Class 2b-7'].D + isr2305ReportTableData['Class 8'].D),
    G:
      WEIGHTED_FACTOR *
      (isr2305ReportTableData['Class 2b-7'].G + isr2305ReportTableData['Class 8'].G),
    NZE:
      WEIGHTED_FACTOR *
      (isr2305ReportTableData['Class 2b-7'].NZE + isr2305ReportTableData['Class 8'].NZE),
    ZE:
      WEIGHTED_FACTOR *
      (isr2305ReportTableData['Class 2b-7'].ZE + isr2305ReportTableData['Class 8'].ZE),
  }

  isr2305ReportTableData['Total WATTs'] =
    isr2305ReportTableData.WATTs.D +
    isr2305ReportTableData.WATTs.G +
    isr2305ReportTableData.WATTs.NZE +
    isr2305ReportTableData.WATTs.ZE

  isr2305ReportTableData['Compliance Period'] = new Date().getFullYear()

  const formatNumber = (value: number | null): string => {
    return typeof value === 'number' ? new Intl.NumberFormat('en-US').format(value) : null
  }

  const onDownload = async (): Promise<void> => {
    const {
      reader,
      meta: { contentLength, fileName },
    } = await apiStreamReader({
      path: '/observations/isr?export=true',
      token: token || '<TOKEN>',
    })
    const chunks: Array<Uint8Array> = []
    let receivedLength = 0
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read()

      if (done) break
      if (value) chunks.push(value)
      receivedLength += value?.length || 0
    }
    let csvData = ''
    chunks.forEach((chunk) => {
      csvData += decodeUTF8(chunk)
    })
    downloadCSV(csvData, fileName)
  }

  const columns: TableProps<ISRTableDataType>['columns'] = [
    {
      key: '1',
      dataIndex: 'type',
      align: 'right',
    },
    {
      key: '2',
      dataIndex: 'diesel',
      title: 'Diesel',
      width: 100,
    },
    {
      key: '3',
      dataIndex: 'gasoline',
      title: 'Gasoline',
      width: 100,
    },
    {
      key: '4',
      dataIndex: 'nze',
      title: 'NZE',
      width: 100,
    },
    {
      key: '5',
      dataIndex: 'ze',
      title: 'ZE',
      width: 100,
    },
    {
      key: '6',
      dataIndex: 'download',
      align: 'center',
      width: 50,
      title: (
        <Tooltip tipText="Download (CSV)">
          <Icon iconKey="Download" color="primary" size={5} onClick={() => onDownload()} />
        </Tooltip>
      ),
    },
  ]

  const isrData: ISRTableDataType[] = [
    {
      key: '1',
      type: 'Class 2b-7',
      diesel: formatNumber(isr2305ReportTableData['Class 2b-7'].D),
      gasoline: formatNumber(isr2305ReportTableData['Class 2b-7'].G),
      nze: formatNumber(isr2305ReportTableData['Class 2b-7'].NZE),
      ze: formatNumber(isr2305ReportTableData['Class 2b-7'].ZE),
    },
    {
      key: '2',
      type: 'Class 8',
      diesel: formatNumber(isr2305ReportTableData['Class 8'].D),
      gasoline: formatNumber(isr2305ReportTableData['Class 8'].G),
      nze: formatNumber(isr2305ReportTableData['Class 8'].NZE),
      ze: formatNumber(isr2305ReportTableData['Class 8'].ZE),
    },
    {
      key: '3',
      type: 'Weighted Annual Truck Trips',
      diesel: formatNumber(isr2305ReportTableData.WATTs.D),
      gasoline: formatNumber(isr2305ReportTableData.WATTs.G),
      nze: formatNumber(isr2305ReportTableData.WATTs.NZE),
      ze: formatNumber(isr2305ReportTableData.WATTs.ZE),
    },
    {
      key: '4',
      type: 'Total WATTs',
      diesel: formatNumber(isr2305ReportTableData['Total WATTs']),
    },
    {
      key: '4',
      type: 'Compliance Period',
      diesel: isr2305ReportTableData['Compliance Period'],
    },
  ]

  return (
    <StyledTableContainer>
      <Table columns={columns} dataSource={isrData} pagination={false} />
    </StyledTableContainer>
  )
}
